import { sumWeights } from '@rematter/common/sumWeights';
import { ensureValue } from '@rematter/ui-web';

import { fullPurchaseOrderFragment } from './fragmentsPurchaseOrder';

import {
  createQueryAtomFactory,
  QueryAtomResult,
} from '~/shared/providers/api/createQueryAtomFactory';

export type FullPurchaseOrderRecord = QueryAtomResult<
  typeof purchaseOrderDetailsQueryAtom
>;

export type FullPurchaseOrderLineItemRecord =
  FullPurchaseOrderRecord['lineItems'][number];

export const purchaseOrderDetailsQueryAtom = createQueryAtomFactory(
  'customerPortal.purchaseOrderDetails',
  (params: { id: string }) => ({
    // For customers, sales orders are purchase orders
    salesOrders: {
      __args: {
        filter: {
          ids: [params.id],
        },
      },
      entries: {
        ...fullPurchaseOrderFragment,
      },
    },
  }),
  {
    select: ({ salesOrders }) => {
      const po = salesOrders.entries.at(0);
      if (!po) return null;
      return {
        id: po.id,
        status: po.status,
        startDate: po.startDate ?? null,
        endDate: po.endDate ?? null,
        paymentCurrency: po.paymentCurrency ?? null,
        paymentTerm: po.paymentTerm ?? null,
        freightTerm: po.freightTerm ?? null,
        customerInternalNotes: po.customerInternalNotes ?? null,
        customerExternalNotes: po.customerExternalNotes ?? null,
        recyclerExternalNotes: po.recyclerExternalNotes ?? null,
        salesOrderNumber: po.documentNumber,
        purchaseOrderNumber: po.counterpartyReferenceNumber ?? null,
        totalPrice: {
          amount: po.totalPrice.amount,
          currencyCode: po.totalPrice.currencyCode,
        },
        netWeight: sumWeights(
          po.lineItems.map((li) => ({
            value: Number(li.quantity),
            unit: li.unit,
          })),
        ),
        accountRepresentative: po.accountRepresentative
          ? {
              firstName: po.accountRepresentative.firstName,
              lastName: po.accountRepresentative.lastName,
            }
          : null,
        contact: po.contact
          ? {
              id: po.contact.id,
              fullName: po.contact.fullName,
              email: po.contact.email ?? null,
              phone: po.contact.phone ?? null,
            }
          : null,
        supplier: po.account
          ? {
              id: po.account.id,
              name: po.account.name,
              addresses: po.account.addresses.map((it) => it.address),
              contacts: po.account.contacts.map((c) => ({
                id: c.id,
                fullName: c.fullName,
              })),
            }
          : null,
        lineItems: po.lineItems.map((it) => ({
          id: ensureValue(it.id),
          unitPrice: {
            amount: it.unitPrice.amount,
            currencyCode: it.unitPrice.currencyCode,
          },
          totalPrice: {
            amount: it.totalPrice.amount,
            currencyCode: it.totalPrice.currencyCode,
          },
          item: {
            id: it.item.id,
            name: it.item.name,
            skuNumber: it.item.skuNumber,
            description: it.item.description ?? null,
            groupName: it.item.itemGroup?.name ?? 'None',
          },
          quantity: { value: Number(it.quantity), unit: it.unit },
        })),
        customerFiles: po.customerFiles,
        recyclerFiles: po.recyclerFiles,
      };
    },
  },
);
